import {
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonToast,
  IonBadge,
  IonRefresher,
  IonRefresherContent,
  RefresherCustomEvent,
  IonSpinner,
} from "@ionic/react";
import { useMemo, useState } from "react";
import { AppHeader } from "../AppHeader";
import { useVisibleOrganisations } from "@/hooks";
import AcceptInvitationModal from "./AcceptInvitationModal";
import RequestAccessModal from "./RequestAccessModal";
import type { OrganisationInvitation, VisibleOrganisation } from "@/types";

export interface ModalProps {
  showModal: boolean;
  handleClose: (props?: {
    invalidate: boolean;
    toast: {
      message: string;
      color: "success" | "danger";
    };
  }) => void;
}

export default function OrganisationsInvitationsModal({
  showModal,
  handleClose,
}: ModalProps) {
  const [tab, setTab] = useState<"organisations" | "invitations">(
    "organisations"
  );
  const [showToast, setShowToast] = useState({
    show: false,
    message: "",
    color: "success" as "success" | "danger",
  });

  // Sub-modal states
  const [selectedOrganisation, setSelectedOrganisation] =
    useState<VisibleOrganisation | null>(null);
  const [selectedInvitation, setSelectedInvitation] =
    useState<OrganisationInvitation | null>(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const { data, refetch } = useVisibleOrganisations();

  const visibleOrganisations = useMemo(() => {
    return data?.organisations ?? [];
  }, [data]);

  const invitations = useMemo(() => {
    return data?.invitations ?? [];
  }, [data]);

  const handleOrganisationClick = (organisation: VisibleOrganisation) => {
    if (organisation.pending_request) {
      // Already has a pending request, show info
      setShowToast({
        show: true,
        message: "Je hebt al een verzoek in behandeling voor deze organisatie.",
        color: "warning" as "success" | "danger",
      });
      return;
    }

    if (organisation.has_invitation) {
      // Find the invitation for this organisation
      const invitation = invitations.find(
        (inv) => inv.organisation_id === organisation.id
      );
      if (invitation) {
        setSelectedInvitation(invitation);
        setShowAcceptModal(true);
      }
    } else {
      // No invitation, open request access modal
      setSelectedOrganisation(organisation);
      setShowRequestModal(true);
    }
  };

  const handleInvitationClick = (invitation: OrganisationInvitation) => {
    setSelectedInvitation(invitation);
    setShowAcceptModal(true);
  };

  const handleAcceptModalClose = (result?: {
    success: boolean;
    message: string;
  }) => {
    setShowAcceptModal(false);
    setSelectedInvitation(null);
    if (result) {
      setShowToast({
        show: true,
        message: result.message,
        color: result.success ? "success" : "danger",
      });
      if (result.success) {
        refetch();
      }
    }
  };

  const handleRequestModalClose = (result?: {
    success: boolean;
    message: string;
  }) => {
    setShowRequestModal(false);
    setSelectedOrganisation(null);
    if (result) {
      setShowToast({
        show: true,
        message: result.message,
        color: result.success ? "success" : "danger",
      });
      if (result.success) {
        refetch();
      }
    }
  };

  function handleIonRefresh(event: RefresherCustomEvent) {
    refetch().finally(() => event.detail.complete());
  }

  return (
    <>
      <IonModal
        isOpen={showModal}
        onDidDismiss={() => handleClose()}
        className="organisations-invitations-modal"
      >
        <AppHeader
          title="Organisaties / Uitnodigingen"
          onClose={() => handleClose()}
        />
        <IonContent className="ion-padding">
          <IonRefresher slot="fixed" onIonRefresh={handleIonRefresh}>
            <IonRefresherContent className="tw:flex tw:justify-center tw:items-center">
              <IonSpinner />
            </IonRefresherContent>
          </IonRefresher>
          <IonText color="medium">
            <p style={{ marginBottom: "16px" }}>
              Hieronder staan organisaties waar je lid van kunt worden.
            </p>
          </IonText>

          <IonSegment
            value={tab}
            onIonChange={(e) =>
              setTab(e.detail.value as "organisations" | "invitations")
            }
          >
            <IonSegmentButton value="organisations">
              <IonLabel>Organisaties</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="invitations">
              <IonLabel>
                Uitnodigingen
                {invitations.length > 0 && (
                  <IonBadge color="primary" style={{ marginLeft: "8px" }}>
                    {invitations.length}
                  </IonBadge>
                )}
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {tab === "organisations" && (
            <div>
              {visibleOrganisations.length === 0 ? (
                <IonText color="medium">
                  <p style={{ textAlign: "center", marginTop: "32px" }}>
                    Geen organisaties gevonden.
                  </p>
                </IonText>
              ) : (
                <IonList>
                  {visibleOrganisations.map((organisation) => (
                    <IonItem
                      key={organisation.id}
                      button
                      onClick={() => handleOrganisationClick(organisation)}
                      detail={!organisation.pending_request}
                    >
                      <IonLabel>
                        <h2>{organisation.name}</h2>
                        {organisation.pending_request && (
                          <p>Verzoek in behandeling</p>
                        )}
                        {organisation.has_invitation &&
                          !organisation.pending_request && (
                            <p>Uitnodiging ontvangen</p>
                          )}
                      </IonLabel>
                      {organisation.pending_request && (
                        <IonBadge color="warning" slot="end">
                          In behandeling
                        </IonBadge>
                      )}
                      {organisation.has_invitation &&
                        !organisation.pending_request && (
                          <IonBadge color="success" slot="end">
                            Uitnodiging
                          </IonBadge>
                        )}
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          )}

          {tab === "invitations" && (
            <div>
              {invitations.length === 0 ? (
                <IonText color="medium">
                  <p style={{ textAlign: "center", marginTop: "32px" }}>
                    Je hebt geen openstaande uitnodigingen.
                  </p>
                </IonText>
              ) : (
                <IonList>
                  {invitations.map((invitation) => (
                    <IonItem
                      key={invitation.id}
                      button
                      onClick={() => handleInvitationClick(invitation)}
                      detail
                    >
                      <IonLabel>
                        <h2>{invitation.organisation.name}</h2>
                        {invitation.description && (
                          <p>{invitation.description}</p>
                        )}
                        {invitation.role && <p>Rol: {invitation.role}</p>}
                      </IonLabel>
                      <IonBadge color="success" slot="end">
                        Uitnodiging
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* Accept Invitation Sub-Modal */}
      <AcceptInvitationModal
        invitation={selectedInvitation}
        isOpen={showAcceptModal}
        onClose={handleAcceptModalClose}
      />

      {/* Request Access Sub-Modal */}
      <RequestAccessModal
        organisation={selectedOrganisation}
        isOpen={showRequestModal}
        onClose={handleRequestModalClose}
      />

      {/* Toast */}
      <IonToast
        isOpen={showToast.show}
        onDidDismiss={() => setShowToast({ ...showToast, show: false })}
        message={showToast.message}
        duration={2000}
        color={showToast.color}
      />
    </>
  );
}
